import * as Sentry from '@sentry/browser';
import {
   sentryErrorIntegration,
   ignoreCloudFrontBlockIntegration,
   ignoreUserAgentIntegration,
   watchForHorizontalOverflow,
   IGNORED_RESOURCE_SPAN_OPS,
   isLikelyBot,
   isUnpaintedPageload,
} from '@ifixit/sentry';
import { createFailedUseHrefReporter } from 'Shared/svg_use_report';

const ERROR_SAMPLE_RATE_PRODUCTION = 0.05;
const ERROR_SAMPLE_RATE_DEV = 0;

const TRACE_SAMPLE_RATE = App.isProduction ? 0.005 : 0;
// Most /Join pageloads are scripted browsers sent there by the login wall.
// Case-insensitive with any subpath, the same match Routes.php uses.
const JOIN_PAGE = /^\/Join(\/|$)/i;

const errorSampleRate = App.isProduction ? ERROR_SAMPLE_RATE_PRODUCTION : ERROR_SAMPLE_RATE_DEV;

let errorsLeftToSend = 1;
let iconReportsLeftToSend = 1;
let overflowReportsLeftToSend = 1;
const forceSentry = App.canIForceSentryReporting && /(forceSentry)/.test(window.location.search);
const performanceOptions = App.sentryPerformance
   ? {
        integrations: [
           Sentry.browserTracingIntegration({ ignoreResourceSpans: IGNORED_RESOURCE_SPAN_OPS }),
           sentryErrorIntegration(),
           ignoreCloudFrontBlockIntegration(),
           ignoreUserAgentIntegration(),
           Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: ['ifixitFirstParty'],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
           }),
        ],
        tracesSampler: ({ inheritOrSampleWith }) =>
           isLikelyBot() || JOIN_PAGE.test(window.location.pathname)
              ? 0
              : inheritOrSampleWith(TRACE_SAMPLE_RATE),
        beforeSendTransaction: event => (isUnpaintedPageload(event) ? null : event),
     }
   : {
        integrations: [
           sentryErrorIntegration(),
           ignoreCloudFrontBlockIntegration(),
           ignoreUserAgentIntegration(),
           Sentry.thirdPartyErrorFilterIntegration({
              filterKeys: ['ifixitFirstParty'],
              behaviour: 'apply-tag-if-exclusively-contains-third-party-frames',
           }),
        ],
     };

let sampleRate = forceSentry ? 1 : errorSampleRate;
let shouldSendToSentry = Math.random() < sampleRate;

Sentry.init({
   dsn: App.SENTRY_DSN_JS,
   environment: App.isProduction ? 'production' : 'dev',
   release: App.sentryRelease,
   ignoreErrors: [
      'TypeError: Network request failed',
      // Lazy chunk fetch failed (network drop, blocked request, etc.); not
      // actionable from app code.
      'ChunkLoadError',
      /Loading (CSS )?chunk \S+ failed/i,
   ],
   ...performanceOptions,

   // We effect our sampling in beforeSend, so we tell sentry to sample all
   // errors
   sampleRate: 1,
   beforeSend: function (event) {
      // Sample-exempt events still respect the dev rate of 0, so a dev
      // page stays silent without ?forceSentry.
      if (event.tags && event.tags.bypassErrorSampling && sampleRate > 0) {
         return event;
      }

      if (!shouldSendToSentry) {
         return null;
      }

      // Icon diagnostics fire before app code can throw; a shared counter
      // would let them spend the one real-error slot every time.
      if (event.tags && event.tags.iconDiagnostic) {
         if (iconReportsLeftToSend <= 0) {
            return null;
         }
         iconReportsLeftToSend--;
         return event;
      }

      if (event.tags && event.tags.overflowDiagnostic) {
         if (overflowReportsLeftToSend <= 0) {
            return null;
         }
         overflowReportsLeftToSend--;
         return event;
      }

      if (errorsLeftToSend <= 0) {
         return null;
      }
      errorsLeftToSend--;
      return event;
   },
   normalizeDepth: 10,
});

Sentry.setTags(App.sentryTags);
Sentry.setUser({ ip_address: '{{auto}}' });

// An icon href the CDN lacks (an FA4 name passed through) fails only in the
// browser. `error` doesn't bubble, but the capture phase descends to target.
const reportFailedUseHref = createFailedUseHrefReporter({
   shouldReport: () => shouldSendToSentry && iconReportsLeftToSend > 0,
   capture: (message, tags) => Sentry.captureMessage(message, { level: 'warning', tags }),
});
document.addEventListener(
   'error',
   ev => {
      // A missing href resolves as a page-relative URL; the only href-less
      // <use> (watch.phtml's same-document #successIcon) never errors anyway.
      const href = ev.target instanceof SVGUseElement && ev.target.getAttribute('href');
      if (href) {
         reportFailedUseHref(href);
      }
   },
   { capture: true }
);
// A same-origin 404 beats this deferred bundle even on a small page, so
// SvgUseErrorQueueComponent's inline head script queues early failures.
(window.__failedSvgUseHrefs || []).forEach(reportFailedUseHref);
// Drained once; drop the inline listener so it stops refilling the queue.
if (window.__svgUseErrorListener) {
   document.removeEventListener('error', window.__svgUseErrorListener, true);
}

// Crowdin's in-context host swaps every string for a placeholder token and
// injects its own markup, so its header overflows on every pageview. Only
// translators see that site, and no layout fix would settle it.
const IN_CONTEXT_LANGID = 'ts';
if (App.sentryTags.lang !== IN_CONTEXT_LANGID) {
   watchForHorizontalOverflow({
      capture: (message, context) => Sentry.captureMessage(message, context),
   });
}
